import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, PenLine } from 'lucide-react'
import AnimatedButton from '../../../components/AnimatedButton'
import MobileBetSheet from '../../../components/MobileBetSheet'
import useCrashStore from '../store/useCrashStore'
import useAppStore from '../../../store/useAppStore'
import { useCrashBettingTimer } from '../hooks/useCrashBettingTimer'
import { hapticImpact, hapticSelectionChanged } from '../../../utils/haptic'
import { PHASES } from '../config/crashConfig'
import CrashCashoutButton from './CrashCashoutButton'

/**
 * CrashActionButton — единая кнопка главного действия под игровым полем
 * (mobile + desktop). Заменила desktop-карточку CrashMyBet: вся информация
 * о своей ставке/исходе — компактно в самой кнопке.
 *
 * Состояния:
 * - betting, своей ставки нет → активная «Поставить», открывает шторку
 *   с MobileBetSheet (только mobile — на desktop ставка делается в sticky-панели,
 *   поэтому этот вариант скрыт lg:hidden);
 * - betting, своя ставка есть → неактивная «Ваша ставка · N»;
 * - running, своя ставка не выведена → активная «Вывести N» (CrashCashoutButton);
 * - своя ставка выведена (running/busted) → неактивная «Выведено +N» (зелёная);
 * - своя ставка не выведена, крах → неактивная «Проиграно −N» (красная);
 * - приём закрыт без своей ставки (не успел поставить) → «Приём ставок закрыт»;
 *   idle → «Подключение…»;
 * - наблюдатель (нет авторизации) и заглушка disabled → блок не рисуется.
 */
const CrashActionButton = () => {
  const { t } = useTranslation()
  const isGuest = useAppStore((s) => s.isGuest)
  const openLogin = useAppStore((s) => s.openLogin)

  const phase = useCrashStore((s) => s.phase)
  const isDisabled = useCrashStore((s) => s.isDisabled)
  const isAuthorized = useCrashStore((s) => s.isAuthorized)
  const isConnected = useCrashStore((s) => s.isConnected)
  const limits = useCrashStore((s) => s.limits)
  const myBet = useCrashStore((s) => s.myBet)
  const myChatId = useCrashStore((s) => s.myChatId)
  const myPlayerState = useCrashStore((s) =>
    myChatId ? s.players.get(String(myChatId)) : null
  )
  const { isExpired } = useCrashBettingTimer()

  const [betSheetOpen, setBetSheetOpen] = useState(false)

  const isBetting = phase === PHASES.BETTING
  const canBet = isBetting && limits && !isExpired && isConnected
  const showPlaceBet = canBet && !myBet
  const myCashed = Boolean(myPlayerState?.cashed)
  const showCashout = phase === PHASES.RUNNING && myBet && !myCashed

  // Шторка живёт только пока можно ставить; закрываем при смене фазы и по своей ставке
  useEffect(() => {
    if (!showPlaceBet) setBetSheetOpen(false)
  }, [showPlaceBet])

  if (isDisabled) return null
  if (isGuest) return <button type="button" onClick={openLogin} className="gradient-border-cta w-full rounded-xl p-4 font-bold text-dark-primary">{t('guest.login')}</button>
  if (!isAuthorized) return null

  // Сумма с иконкой кристалла (Gem) — без знаков «+»/«−», тон иконки наследует текст
  const amountLabel = (n) => (
    <span className="inline-flex items-center gap-1">
      <Gem className="w-3.5 h-3.5" />
      {Number(n || 0).toLocaleString('ru-RU')}
    </span>
  )

  // Текст неактивной кнопки по фазе/состоянию своей ставки.
  // Сначала — исход своей ставки (победа/поражение помним до конца раунда),
  // «Приём ставок закрыт» — только если своей ставки не было (не успел поставить).
  const { disabledLabel, disabledTone } = (() => {
    if (isBetting && myBet) {
      return {
        disabledLabel: <>{t('crash.buttons.placed')} {amountLabel(myBet.amount)}</>,
        disabledTone: 'neutral'
      }
    }
    if (myBet && myCashed) {
      return {
        disabledLabel: <>{t('crash.buttons.cashedOut')} {amountLabel(myPlayerState?.win)}</>,
        disabledTone: 'win'
      }
    }
    if (myBet && !myCashed && phase === PHASES.BUSTED) {
      return {
        disabledLabel: <>{t('crash.buttons.lost')} {amountLabel(myBet.amount)}</>,
        disabledTone: 'loss'
      }
    }
    if (!isConnected) {
      return { disabledLabel: t('realtime.reconnecting'), disabledTone: 'neutral' }
    }
    if (phase === PHASES.IDLE) {
      return { disabledLabel: t('crash.notices.connecting'), disabledTone: 'neutral' }
    }
    return { disabledLabel: t('crash.alerts.bet_closed'), disabledTone: 'neutral' }
  })()

  return (
    <>
      <div>
        {showPlaceBet ? (
          // «Поставить» — только mobile: на desktop ставка ставится в sticky-панели
          <AnimatedButton
            onClick={() => setBetSheetOpen(true)}
            className="w-full lg:hidden"
            soundFeedback={false}
          >
            {t('crash.bet.submit')}
          </AnimatedButton>
        ) : showCashout ? (
          <CrashCashoutButton />
        ) : (
          <button
            disabled
            className={`
              w-full py-3 rounded-xl font-bold tabular-nums bg-surface cursor-not-allowed
              flex items-center justify-center gap-1.5
              ${disabledTone === 'win'
                ? 'border border-accent-primary/40 text-accent-primary'
                : disabledTone === 'loss'
                  ? 'border border-red-500/40 text-red-400'
                  : 'border border-white/10 text-text-secondary'}
            `}
          >
            {disabledLabel}
          </button>
        )}
      </div>

      {/* Шторка с формой ставки: закрытие — тап по фону или кнопка в шапке */}
      <CrashBetPanel isOpen={betSheetOpen && Boolean(showPlaceBet)} canPlaceBet={Boolean(showPlaceBet)} onClose={() => { hapticImpact('light'); setBetSheetOpen(false) }} />
    </>
  )
}

export const CrashBetPanel = ({ variant, isOpen = true, canPlaceBet = false, onClose }) => {
  const { t } = useTranslation()
  const limits = useCrashStore((s) => s.limits)
  const roundId = useCrashStore((s) => s.roundId)
  const balance = useCrashStore((s) => s.balance)
  const inventory = useCrashStore((s) => s.inventory)
  const isPlacingBet = useCrashStore((s) => s.isPlacingBet)
  const isConnected = useCrashStore((s) => s.isConnected)
  const isAuthorized = useCrashStore((s) => s.isAuthorized)
  const lastAlertCode = useCrashStore((s) => s.lastAlertCode)
  const placeBet = useCrashStore((s) => s.placeBet)
  const [autoCashout, setAutoCashout] = useState(() => localStorage.getItem('crash.autoCashout') || '')
  const [manualAuto, setManualAuto] = useState(() => {
    const saved = localStorage.getItem('crash.autoCashout') || ''
    return saved !== '' && ![1.5, 2, 3, 5, 10].includes(Number(saved))
  })
  const parsedAuto = Number(autoCashout) || 0
  const minAuto = Number(limits?.min_auto_cashout || 101)
  const maxAuto = Number(limits?.max_auto_cashout || 100000)
  const invalidAuto = autoCashout !== '' && parsedAuto > 0 &&
    (Math.floor(parsedAuto * 100) < minAuto || Math.floor(parsedAuto * 100) > maxAuto)
  const autoError = invalidAuto ? t('crash.bet.errorAutoCashout', {
    min: (minAuto / 100).toFixed(2), max: (maxAuto / 100).toFixed(2)
  }) : ''
  const alertKeys = {
    bet_closed: 'bet_closed', bad_amount: 'bad_amount', bad_auto_cashout: 'bad_auto_cashout',
    already_bet: 'already_bet', not_enough_balance: 'not_enough_balance_with_topup',
    round_not_betting: 'round_not_betting', mode_disabled: 'mode_disabled',
    streamer_restricted: 'streamer_restricted', items_not_available: 'items_not_available',
    too_many_items: 'too_many_items', items_disabled: 'items_disabled',
    user_not_found: 'server_error', bet_failed: 'server_error', server_error: 'server_error'
  }
  const serverError = alertKeys[lastAlertCode] ? t(`crash.alerts.${alertKeys[lastAlertCode]}`) : ''

  useEffect(() => {
    if (autoCashout !== '' && Number(autoCashout) > 0) localStorage.setItem('crash.autoCashout', autoCashout)
    else localStorage.removeItem('crash.autoCashout')
  }, [autoCashout])

  return <MobileBetSheet
    variant={variant}
    isOpen={isOpen}
    mode="crash"
    resetKey={roundId}
    balance={balance}
    inventory={limits?.items_enabled ? inventory : []}
    minBet={limits?.min_bet ?? 100}
    maxBet={limits?.max_bet ?? 100000}
    maxItems={limits?.max_items ?? 10}
    isDataReady={Boolean(limits)}
    isLoading={isPlacingBet}
    isDisabled={!canPlaceBet || !isConnected || !isAuthorized || invalidAuto}
    disabledReason={!isConnected ? t('realtime.reconnecting') : !isAuthorized ? t('crash.notices.observer') : !canPlaceBet ? t('crash.alerts.bet_closed') : autoError}
    externalError={autoError || serverError}
    onClose={onClose}
    onSubmit={({ crystal, items }) => {
      if (placeBet({ amount: crystal, items, autoCashout: parsedAuto })) hapticImpact('heavy')
      return false
    }}
    extraContent={
      <section className="border-t border-white/10 pt-5">
        <h3 className="mb-3 font-semibold text-white">{t('crash.bet.autoCashout')}</h3>
        <div className="flex flex-wrap gap-2">
          {[null, 1.5, 2, 3, 5, 10].map((preset) => {
            const active = !manualAuto && (preset === null ? autoCashout === '' : parsedAuto === preset && autoCashout !== '')
            return <button key={preset ?? 'off'} type="button" aria-pressed={active} onClick={() => {
              hapticSelectionChanged()
              setAutoCashout(preset === null ? '' : String(preset))
              setManualAuto(false)
            }} className={`rounded-lg border px-3 py-2 text-xs font-semibold tabular-nums active:scale-[0.98] ${active ? 'border-accent-primary/50 bg-accent-primary/15 text-accent-primary' : 'border-white/10 bg-surface text-text-secondary'}`}>
              {preset === null ? t('crash.bet.autoCashoutOff') : `${preset}×`}
            </button>
          })}
          <button type="button" aria-pressed={manualAuto} onClick={() => { hapticSelectionChanged(); setManualAuto(true) }} aria-label={t('crash.bet.autoCashoutCustom')} className={`rounded-lg border px-3 py-2 text-xs active:scale-[0.98] ${manualAuto ? 'border-accent-primary/50 bg-accent-primary/15 text-accent-primary' : 'border-white/10 bg-surface text-text-secondary'}`}>
            <PenLine className="h-4 w-4" />
          </button>
        </div>
        {manualAuto && <input type="number" inputMode="decimal" step="0.01" min={(minAuto / 100).toFixed(2)} max={(maxAuto / 100).toFixed(2)} value={autoCashout} onChange={(event) => setAutoCashout(event.target.value)} placeholder={t('crash.bet.autoCashoutPlaceholder', { min: (minAuto / 100).toFixed(2), max: (maxAuto / 100).toFixed(2) })} className="mt-3 w-full rounded-xl border border-white/10 bg-surface px-3 py-3 text-white outline-none focus:border-accent-primary/50" />}
      </section>
    }
  />
}

export default CrashActionButton
